import { useQuery } from 'react-query';
import { fetchTrackerBoard } from '../../../services/tracker-api-client';
import { trackerBoardKey } from '../../../lib/groupQueryKeys';
import { RESOLVED_AUDIENCES } from './audience';
import type { TrackerGuest } from '../../../services/tracker-api-client';

/**
 * The resolved roster, for the audiences that cannot be computed without it.
 *
 * `group.invitees` carries `group_invitees.rsvp_status` alone. The value every
 * host-facing surface shows is `going_override ?? map(rsvp_status)`, and the
 * override lives in `group_guest_tracking` — a different table, written by the
 * Guests tab's Going dropdown, which never touches rsvp_status.
 *
 * So a host who has marked forty guests Going has changed nothing
 * `group.invitees` can see. Resolving "no reply" from it would have put those
 * forty on the chase list and told them they had not replied.
 *
 * Only the RSVP audiences need this. "All guests" and the roster segments are
 * decided server-side, so the fetch is not on their critical path.
 */
export interface BroadcastRoster {
  /** Null until loaded, and on failure. Never a partial or substituted list. */
  guests: TrackerGuest[] | null;
  /** True when the chosen audience needs the roster and does not have it. */
  missing: boolean;
  /** What to say under the audience field, or '' when there is nothing to say. */
  hint: string;
}

export function useBroadcastRoster(groupId: string, audience: string): BroadcastRoster {
  const needed = RESOLVED_AUDIENCES.has(audience);
  const query = useQuery(trackerBoardKey(groupId), () => fetchTrackerBoard(groupId), {
    // Only for the audiences that use it. A host who never picks one should
    // not pay for a roster fetch, and a failure they cannot act on should not
    // appear under a field it does not affect.
    enabled: needed,
  });

  const guests = query.data?.ok ? query.data.data.guests : null;
  /*
    Missing means DO NOT SEND, and the composer treats it that way.

    Falling back to `group.invitees` would silently answer a different
    question. Falling back to no id filter is worse: the API reads that as
    everyone, so a failed fetch would turn "chase the 97" into "text all 137".
  */
  const missing = needed && !guests;

  return {
    guests,
    missing,
    hint: !missing
      ? ''
      : query.isLoading
        ? 'Reading the roster to work out who that is…'
        : 'Could not read the roster, so this audience cannot be worked out. Reload and try again.',
  };
}
